import type React from "react";
import { useState } from "react";

import {
	useCurrentSongSelector,
	useLastSongSelector,
	saveCurrentSong,
	saveLastSong,
} from "@redux/player";
import analytics from "lib/analytics";
import { useDispatch } from "react-redux";
import { useInterval } from "react-use";

import {
	HALF_HOUR_MS,
	TEN_MINUTES_MS,
	TEN_SECONDS_MS,
	THIRTY_SECONDS_MS,
	TWEENTY_MINUTES_MS,
} from "constants/lastSong";
import {
	secondsToHoursAndMinutes,
	SECONDS_TO_MS_RATIO,
} from "services/helpers/formatTime";
import {
	sendMultiScreenEventToIpad,
	sendMultiScreenEventToTv,
} from "services/helpers/multiScreen";
import { useClearSession } from "services/hooks/useClearSession";
import { PlayerEvent } from "shared-types/player-event";

import { StyledRemainingTime } from "./RemainingTime.style";
import { useWaitingListData } from "services/hooks/useWaitingListData";

interface Props {
	sessionEndTimestamp: number;
}

const RemainingTime: React.FC<Props> = ({ sessionEndTimestamp }) => {
	const [remainingTime, setRemainingTime] = useState<number>(
		sessionEndTimestamp - Date.now(),
	);
	const clearSession = useClearSession();

	const lastSong = useLastSongSelector();
	const currentSong = useCurrentSongSelector();
	const { splitWaitingList } = useWaitingListData();

	const lastSongStartTimeMs = lastSong.song?.length
		? lastSong.song.length * 1000
		: 0;

	const dispatch = useDispatch();

	const remainingTimeEvents = (remainingTimeInMs: number) => {
		const timeWindows = [
			{
				duration: HALF_HOUR_MS,
				event: PlayerEvent.SHOW_LAST_30_MINUTES_TV_MESSAGE,
			},
			{
				duration: TWEENTY_MINUTES_MS,
				event: PlayerEvent.SHOW_LAST_20_MINUTES_TV_MESSAGE,
			},
			{
				duration: TEN_MINUTES_MS,
				event: PlayerEvent.SHOW_LAST_10_MINUTES_TV_MESSAGE,
			},
		];

		for (const { duration, event } of timeWindows) {
			const lowerBound = duration - THIRTY_SECONDS_MS;

			if (remainingTimeInMs <= duration && remainingTimeInMs > lowerBound) {
				sendMultiScreenEventToTv(event);
				return;
			}

			if (
				remainingTimeInMs <= lowerBound &&
				remainingTimeInMs > lowerBound - 1000
			) {
				// Just passed the 30s window: clear the message
				sendMultiScreenEventToTv(PlayerEvent.CLEAR_TV_MESSAGE);
				return;
			}
		}

		// Handle last song related events
		if (lastSong && lastSongStartTimeMs) {
			const tenSecondsBeforeLastSong = lastSongStartTimeMs + TEN_SECONDS_MS;

			// We are in the last 10 seconds of the last song
			if (
				remainingTimeInMs <= tenSecondsBeforeLastSong &&
				remainingTimeInMs > lastSongStartTimeMs
			) {
				sendMultiScreenEventToTv(PlayerEvent.SHOW_LAST_SONG_TV_MESSAGE);
				return;
			}

			// We launch the last song
			const isLastSongStarting =
				remainingTimeInMs <= lastSongStartTimeMs ||
				(splitWaitingList.elapsedTimeWaitingList.length > 0 &&
					splitWaitingList.playableWaitingList.length === 0);

			const isNotCurrentSong = lastSong?.song?.id !== currentSong?.id;

			if (isLastSongStarting && isNotCurrentSong) {
				sendMultiScreenEventToTv(PlayerEvent.CLEAR_TV_MESSAGE);
				analytics.track("launching_last_song", {
					songId: lastSong?.song?.bamId,
					remainingTime,
					lastSongDuration: lastSong?.song?.length,
				});
				dispatch(saveCurrentSong(lastSong.song));
				sendMultiScreenEventToTv(PlayerEvent.PLAY, null);
				dispatch(saveLastSong(null));
				sendMultiScreenEventToTv(PlayerEvent.PLAY_LAST_SONG);
				return;
			}
		}
	};

	useInterval(() => {
		const remainingMs = sessionEndTimestamp - Date.now();

		if (remainingMs <= 0) {
			clearSession();
			sendMultiScreenEventToTv(PlayerEvent.END_SESSION);
			sendMultiScreenEventToIpad(PlayerEvent.END_SESSION);
			sendMultiScreenEventToIpad(PlayerEvent.SET_SESSION_END_OVERLAY);
		} else {
			remainingTimeEvents(remainingMs);
			setRemainingTime(remainingMs);
		}
	}, 1000);

	return (
		<StyledRemainingTime>
			{/* we use UTC to display correctly the time difference */}
			{secondsToHoursAndMinutes(remainingTime / SECONDS_TO_MS_RATIO)}
		</StyledRemainingTime>
	);
};

export default RemainingTime;
